import {FC, useEffect, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import toaster from '../../../../Utils/toaster'
import {
  checkPlunetCustomer,
  createOrLinkPlunetCustomer,
  checkPlunetContact,
  createOrLinkPlunetContact,
  getPlunetApiErrorMessage,
} from '../../../pages/plunet/plunet-api'
import {
  PlunetCustomerCheckResponse,
  PlunetContactCheckResponse,
} from '../../../pages/plunet/plunet-types'

export type PlunetAccountModalProps = {
  type: 'customer' | 'contact'
  id?: string | number
  name: string
  email: string
  address?: string
  customerName?: string
  customerPlunetId?: number | null
  onClose: () => void
  onSuccess: (newPlunetId: number) => void
}

export const PlunetAccountModal: FC<PlunetAccountModalProps> = ({
  type,
  id,
  name,
  email,
  address,
  customerName,
  customerPlunetId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [confirmStep, setConfirmStep] = useState(false)
  const [existingCustomerData, setExistingCustomerData] = useState<PlunetCustomerCheckResponse | null>(null)
  const [existingContactData, setExistingContactData] = useState<PlunetContactCheckResponse | null>(null)

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  const isContact = type === 'contact'
  const isCustomerMissingPlunet = isContact && (!customerPlunetId || customerPlunetId <= 0)

  const handleCreateOrCheck = async () => {
    if (!email) {
      toaster('error', 'Email address is required to create a Plunet account.')
      return
    }

    try {
      setLoading(true)

      if (!isContact) {
        // Customer check
        const checkRes = await checkPlunetCustomer(Number(id), email)
        if (checkRes?.exists) {
          setExistingCustomerData(checkRes)
          setConfirmStep(true)
          return
        }

        // Customer creation
        const createRes = await createOrLinkPlunetCustomer(Number(id), false)
        if (createRes?.success && createRes?.plunetId) {
          toaster('success', 'Customer account created on Plunet successfully.')
          onSuccess(createRes.plunetId)
          onClose()
        } else {
          toaster('error', createRes?.error || 'Failed to create customer on Plunet.')
        }
      } else {
        // Contact check
        const checkRes = await checkPlunetContact(String(id), email)
        if (checkRes?.exists) {
          setExistingContactData(checkRes)
          setConfirmStep(true)
          return
        }

        // Contact creation
        const createRes = await createOrLinkPlunetContact(String(id), false)
        if (createRes?.success && createRes?.plunetContactId) {
          toaster('success', 'Contact person created on Plunet successfully.')
          onSuccess(createRes.plunetContactId)
          onClose()
        } else {
          toaster('error', createRes?.error || 'Failed to create contact person on Plunet.')
        }
      }
    } catch (err: unknown) {
      const msg = getPlunetApiErrorMessage(err, 'An error occurred while connecting to Plunet.')
      toaster('error', msg)
    } finally {
      setLoading(false)
    }
  }

  const handleProceedConnect = async () => {
    try {
      setLoading(true)
      if (!isContact && existingCustomerData?.plunetId) {
        const linkRes = await createOrLinkPlunetCustomer(
          Number(id),
          true,
          existingCustomerData.plunetId
        )
        if (linkRes?.success && linkRes?.plunetId) {
          toaster('success', 'Customer connected to existing Plunet account successfully.')
          onSuccess(linkRes.plunetId)
          onClose()
        } else {
          toaster('error', linkRes?.error || 'Failed to connect customer.')
        }
      } else if (isContact && existingContactData?.plunetContactId) {
        const linkRes = await createOrLinkPlunetContact(
          String(id),
          true,
          existingContactData.plunetContactId
        )
        if (linkRes?.success && linkRes?.plunetContactId) {
          toaster('success', 'Contact person connected to existing Plunet account successfully.')
          onSuccess(linkRes.plunetContactId)
          onClose()
        } else {
          toaster('error', linkRes?.error || 'Failed to connect contact person.')
        }
      }
    } catch (err: unknown) {
      const msg = getPlunetApiErrorMessage(err, 'An error occurred while linking to Plunet.')
      toaster('error', msg)
    } finally {
      setLoading(false)
    }
  }

  const oliveBtnStyle = {
    backgroundColor: '#b4b358',
    borderColor: '#b4b358',
    color: '#ffffff',
  }

  return (
    <>
      <div
        className='modal fade show d-block'
        id='kt_modal_plunet_account'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
        style={{zIndex: 1060}}
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content shadow'>
            {/* Modal Header */}
            <div className='modal-header border-0 pb-0 d-flex justify-content-between align-items-start'>
              <div>
                <h3 className='modal-title fw-bolder fs-3 text-dark mb-1'>
                  {confirmStep
                    ? isContact
                      ? 'Contact Person Already Exists'
                      : 'Customer Already Exists'
                    : isContact
                    ? 'Create Plunet Account for below Contact Person'
                    : 'Create Plunet Account for below Client'}
                </h3>
                <p className='text-muted fs-7 mb-0'>
                  {confirmStep
                    ? isContact
                      ? 'A contact person with this email is already available for this customer on Plunet.'
                      : 'A customer account with this email is already available on Plunet.'
                    : isContact
                    ? 'The contact person account will be created with the below details on Plunet.'
                    : 'The customer account will be created with the below details on Plunet.'}
                </p>
              </div>
              <button
                type='button'
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                onClick={onClose}
                disabled={loading}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
              </button>
            </div>

            {/* Modal Body */}
            <div className='modal-body pt-4 pb-5'>
              {!confirmStep ? (
                <>
                  {isCustomerMissingPlunet && (
                    <div className='alert alert-warning d-flex align-items-center p-3 mb-4'>
                      <i className='bi bi-exclamation-triangle-fill text-warning fs-3 me-3'></i>
                      <div className='fs-7'>
                        The associated Customer does not have a Plunet account linked yet. Please create or link the Customer&apos;s Plunet account first.
                      </div>
                    </div>
                  )}

                  <div className='card bg-light border p-4'>
                    <h6 className='fw-bold text-gray-700 mb-4'>
                      {isContact ? 'Contact Person Information' : 'Client Information'}
                    </h6>

                    <div className='row mb-3'>
                      <label className='col-sm-4 fw-bold text-gray-800 fs-7'>
                        {isContact ? 'Contact Name:' : 'Client Name:'}
                      </label>
                      <div className='col-sm-8 text-gray-800 fs-7 fw-semibold'>
                        {name || '-'}
                      </div>
                    </div>

                    <div className='row mb-3'>
                      <label className='col-sm-4 fw-bold text-gray-800 fs-7'>
                        {isContact ? 'Contact Email Address:' : 'Client Email Address:'}
                      </label>
                      <div className='col-sm-8 text-gray-800 fs-7 fw-semibold'>
                        {email || '-'}
                      </div>
                    </div>

                    {!isContact && (
                      <div className='row mb-2'>
                        <label className='col-sm-4 fw-bold text-gray-800 fs-7'>
                          Client Address:
                        </label>
                        <div className='col-sm-8 text-gray-800 fs-7 fw-semibold'>
                          {address || '-'}
                        </div>
                      </div>
                    )}

                    {isContact && (
                      <div className='row mb-2'>
                        <label className='col-sm-4 fw-bold text-gray-800 fs-7'>
                          Company Name:
                        </label>
                        <div className='col-sm-8 text-gray-800 fs-7 fw-semibold'>
                          {customerName || '-'}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Confirmation Popup view */
                <div className='text-center py-4'>
                  <div className='mb-4'>
                    <i className='bi bi-info-circle text-primary' style={{fontSize: '48px'}}></i>
                  </div>
                  <h5 className='fw-bold mb-3'>
                    {isContact
                      ? 'Contact person already available with this email, do you want to connect?'
                      : 'Customer already available with this email, do you want to connect?'}
                  </h5>
                  <p className='text-muted fs-7 mb-0'>
                    {isContact ? (
                      <>
                        <strong>Existing Contact:</strong> {existingContactData?.contactName || email}{' '}
                        (Plunet Contact ID: {existingContactData?.plunetContactId})
                      </>
                    ) : (
                      <>
                        <strong>Existing Customer:</strong> {existingCustomerData?.plunetCustomerName || email}{' '}
                        (Plunet ID: {existingCustomerData?.plunetId})
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className='modal-footer border-0 pt-0 d-flex justify-content-end'>
              {!confirmStep ? (
                <>
                  <button
                    type='button'
                    className='btn btn-secondary btn-sm me-3'
                    onClick={onClose}
                    disabled={loading}
                  >
                    Close
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm fw-bold'
                    style={oliveBtnStyle}
                    onClick={handleCreateOrCheck}
                    disabled={loading || isCustomerMissingPlunet}
                  >
                    {loading ? (
                      <span>
                        Please wait...{' '}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    ) : (
                      'Create Plunet Account'
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type='button'
                    className='btn btn-light btn-sm me-3'
                    onClick={() => setConfirmStep(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary btn-sm fw-bold'
                    onClick={handleProceedConnect}
                    disabled={loading}
                  >
                    {loading ? (
                      <span>
                        Connecting...{' '}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    ) : (
                      'Proceed'
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' style={{zIndex: 1050}}></div>
    </>
  )
}
